import gsap from 'gsap';

const padNumber = (value) => String(value).padStart(2, '0');
const PIN_CLASS = 'home-ecommerce--pinned';
const SLIDE_TRANSITION_MS = 700;
const getSlideScrollWeight = (index) => (index === 1 ? 0.42 : 0.56);
const getScrollerElement = (ctx) => {
  if (ctx.lenisInstance && typeof ctx.lenisInstance.getScrollerElement === 'function') {
    const element = ctx.lenisInstance.getScrollerElement();
    if (element) {
      return element;
    }
  }

  return document.scrollingElement || document.documentElement;
};

const setTitleId = (slide, shouldHaveId) => {
  const title = slide.querySelector('.home-ecommerce__title');
  if (!title) {
    return;
  }

  if (shouldHaveId) {
    title.setAttribute('id', 'home-ecommerce-title');
  } else if (title.id === 'home-ecommerce-title') {
    title.removeAttribute('id');
  }
};

const setActiveSlide = (slides, index, currentIndicator, onActiveSlideChange, state = {}) => {
  const boundedIndex = Math.max(0, Math.min(index, slides.length - 1));
  const previousIndex = Number.isInteger(state.activeIndex) ? state.activeIndex : -1;
  const previousSlide = previousIndex >= 0 ? slides[previousIndex] : null;

  if (state.transitionTimer) {
    window.clearTimeout(state.transitionTimer);
    state.transitionTimer = null;
  }

  slides.forEach((slide, slideIndex) => {
    const isActive = slideIndex === boundedIndex;
    const isPrevious = slideIndex === previousIndex && previousIndex !== boundedIndex;

    slide.classList.toggle('is-active', isActive);
    slide.classList.toggle('is-exiting', isPrevious);
    slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    setTitleId(slide, isActive);
  });

  if (previousSlide && previousIndex !== boundedIndex) {
    state.transitionTimer = window.setTimeout(() => {
      previousSlide.classList.remove('is-exiting');
    }, SLIDE_TRANSITION_MS);
  }

  state.activeIndex = boundedIndex;

  if (currentIndicator) {
    currentIndicator.textContent = padNumber(boundedIndex + 1);
  }

  if (typeof onActiveSlideChange === 'function') {
    onActiveSlideChange(slides[boundedIndex], boundedIndex);
  }
};

export function initEcommerceScroll(ctx) {
  const slideState = {
    activeIndex: 0,
    transitionTimer: null,
  };
  const {
    ecommerceSection,
    ecommerceSlidesWrapper,
    ecommerceSlides,
    ecommerceProgressTrack,
    ecommerceProgressCurrent,
    ecommerceProgressTotal,
    ecommerceScrollTimeline,
  } = ctx;

  const removePinClass = () => {
    ecommerceSection?.classList.remove(PIN_CLASS);
  };

  if (!ecommerceSection || !ecommerceSlides.length || !ecommerceProgressTrack || !ecommerceProgressCurrent) {
    return ctx;
  }

  if (ecommerceScrollTimeline) {
    if (ecommerceScrollTimeline.scrollTrigger) {
      ecommerceScrollTimeline.scrollTrigger.kill();
    }

    ecommerceScrollTimeline.kill();
    ctx.ecommerceScrollTimeline = null;
  }

  const totalSlides = ecommerceSlides.length;
  const progressElement = ecommerceProgressTrack.closest('.home-ecommerce__progress');
  const moveProgressToTarget = (activeSlide) => {
    if (!progressElement) {
      return;
    }

    const isMobileLayout = typeof window !== 'undefined'
      && window.matchMedia('(max-width: 959px)').matches;

    if (isMobileLayout) {
      const textContainer = activeSlide?.querySelector('.home-ecommerce__text');
      if (textContainer && progressElement.parentElement !== textContainer) {
        textContainer.appendChild(progressElement);
      }
      return;
    }

    if (ecommerceSlidesWrapper && progressElement.parentElement !== ecommerceSlidesWrapper) {
      ecommerceSlidesWrapper.appendChild(progressElement);
    }
  };
  const updateProgressPlacement = () => {
    const activeSlide = ecommerceSlides.find((slide) => slide.classList.contains('is-active'))
      || ecommerceSlides[0];
    moveProgressToTarget(activeSlide);
  };

  removePinClass();
  if (ecommerceProgressTotal) {
    ecommerceProgressTotal.textContent = padNumber(totalSlides);
  }
  gsap.set(ecommerceProgressTrack, { scaleX: 0, transformOrigin: 'left center' });
  setActiveSlide(ecommerceSlides, 0, ecommerceProgressCurrent, moveProgressToTarget, slideState);
  updateProgressPlacement();

  if (totalSlides === 1) {
    gsap.set(ecommerceProgressTrack, { scaleX: 1 });
    return ctx;
  }

  const scrollTriggerEnd = () => {
    const height = typeof window === 'undefined' ? 0 : window.innerHeight;
    const compactDistance = height * Math.max(0.95, totalSlides * 0.34);
    return `+=${compactDistance}`;
  };

  const scroller = getScrollerElement(ctx);
  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: ecommerceSection,
      scroller,
      pin: ecommerceSection,
      start: 'center center',
      end: scrollTriggerEnd,
      scrub: 0.35,
      anticipatePin: 1,
      onToggle: (self) => {
        if (ecommerceSection) {
          ecommerceSection.classList.toggle(PIN_CLASS, self.isActive);
        }
      },
      invalidateOnRefresh: true,
    },
  });

  const applySpacerBackground = () => {
    const spacer = timeline.scrollTrigger?.pinSpacer;
    if (spacer) {
      spacer.style.background = '#ffffff';
    }
  };

  applySpacerBackground();
  timeline.scrollTrigger?.addEventListener?.('refreshInit', applySpacerBackground);
  timeline.scrollTrigger?.addEventListener?.('refresh', () => {
    applySpacerBackground();
    updateProgressPlacement();
  });
  timeline.scrollTrigger?.addEventListener?.('kill', removePinClass);
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateProgressPlacement);
  }

  const slideScrollWeights = ecommerceSlides.map((_, index) => getSlideScrollWeight(index));
  let slideCursor = 0;

  ecommerceSlides.forEach((_, index) => {
    const progressValue = (index + 1) / totalSlides;
    const slideDuration = slideScrollWeights[index] || 1;
    const slideStart = slideCursor;
    const slideEnd = slideStart + slideDuration;
    const setActiveTime = Math.max(0, slideStart + (slideDuration * 0.08));
    timeline.call(
      () => setActiveSlide(ecommerceSlides, index, ecommerceProgressCurrent, moveProgressToTarget, slideState),
      null,
      setActiveTime
    );
    timeline.to(
      ecommerceProgressTrack,
      {
        scaleX: progressValue,
        ease: 'none',
        duration: slideDuration,
        immediateRender: false,
      },
      slideStart
    );

    slideCursor = slideEnd;
  });

  ctx.ecommerceScrollTimeline = timeline;
  return ctx;
}
